import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal, Tag } from 'antd';
import cn from 'classnames';

import type { MigrationPlan } from '../../model/types/plan-migration';

import cls from './migration-plan-card.module.css';

type BillingPeriod = 'monthly' | 'yearly';

const PLAN_PRICES: Record<string, { monthly: string; yearly: string }> = {
    basic: { monthly: '19.97', yearly: '203.49' },
    premium: { monthly: '34.97', yearly: '356.69' },
    pro: { monthly: '69.97', yearly: '713.69' },
};

// Founders Club yearly coupon applies a fixed amount_off on the first invoice,
// equivalent to 3 months of the 50% monthly discount. Keep in sync with backend
// FOUNDERS_CLUB_ANNUAL_COUPONS (settings/base.py).
const FOUNDERS_YEARLY_AMOUNT_OFF: Record<string, number> = {
    basic: 25,
    premium: 43.75,
    pro: 62.5,
};

const PLAN_META: Record<
    string,
    {
        creditsKey: 'credits' | 'creditsUnlimited';
        creditsCount: number | null;
        accountsKey: 'oneAccount' | 'unlimitedAccounts';
    }
> = {
    basic: {
        creditsKey: 'credits',
        creditsCount: 800,
        accountsKey: 'oneAccount',
    },
    premium: {
        creditsKey: 'credits',
        creditsCount: 1600,
        accountsKey: 'unlimitedAccounts',
    },
    pro: {
        creditsKey: 'creditsUnlimited',
        creditsCount: null,
        accountsKey: 'unlimitedAccounts',
    },
};

const INITIAL_FEATURES_COUNT = 5;

function getBillingValues(billingPeriod: BillingPeriod, plan: MigrationPlan) {
    const isYearly = billingPeriod === 'yearly';
    const priceId = isYearly ? plan.yearly_price_id : plan.monthly_price_id;
    const prices = PLAN_PRICES[plan.key];
    const displayPrice = isYearly ? prices?.yearly : prices?.monthly;

    return { priceId, displayPrice, isYearly };
}

function computeDiscountedMonthly(
    fullPrice: string,
    discountPercent: number,
): string | null {
    const numeric = parseFloat(fullPrice);

    if (Number.isNaN(numeric) || discountPercent <= 0) {
        return null;
    }

    const discounted = numeric * ((100 - discountPercent) / 100);

    return discounted.toFixed(2);
}

function computeDiscountedYearly(
    fullPrice: string,
    planKey: string,
): string | null {
    const numeric = parseFloat(fullPrice);
    const amountOff = FOUNDERS_YEARLY_AMOUNT_OFF[planKey];

    if (Number.isNaN(numeric) || !amountOff) {
        return null;
    }

    const discounted = Math.max(0, numeric - amountOff);

    return discounted.toFixed(2);
}

interface FeatureRowProps {
    text: string;
}

function FeatureRow({ text }: FeatureRowProps) {
    return (
        <li className={cls.featureRow}>
            <span className={cls.featureIconYes}>
                <svg
                    fill="currentColor"
                    height="12"
                    viewBox="0 0 20 20"
                    width="12"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        clipRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        fillRule="evenodd"
                    />
                </svg>
            </span>
            <span className={cls.featureLabel}>{text}</span>
        </li>
    );
}

interface MetaRowProps {
    text: string;
    iconPath: string;
}

function MetaRow({ text, iconPath }: MetaRowProps) {
    return (
        <div className={cls.metaRow}>
            <svg
                className={cls.metaIcon}
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d={iconPath}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
            <span className={cls.metaText}>{text}</span>
        </div>
    );
}

const CREDITS_ICON =
    'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z';
const ACCOUNTS_ICON =
    'M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z';

interface PriceBlockProps {
    discountedPrice: string | null;
    displayPrice: string | undefined;
    periodLabel: string;
}

function PriceBlock({
    discountedPrice,
    displayPrice,
    periodLabel,
}: PriceBlockProps) {
    const shownPrice = `€${discountedPrice ?? displayPrice}`;
    const amountClassName = discountedPrice
        ? cls.priceAmountDiscounted
        : cls.priceAmount;

    return (
        <div className={cls.priceSection}>
            {discountedPrice && (
                <span className={cls.priceStrikethrough}>
                    {`€${displayPrice}`}
                </span>
            )}
            <span className={amountClassName}>{shownPrice}</span>
            <span className={cls.pricePeriod}>{periodLabel}</span>
        </div>
    );
}

interface MetaBoxProps {
    creditsText: string | null;
    accountsText: string | null;
}

function MetaBox({ creditsText, accountsText }: MetaBoxProps) {
    if (!creditsText && !accountsText) {
        return null;
    }

    return (
        <div className={cls.metaBox}>
            {creditsText && (
                <MetaRow
                    iconPath={CREDITS_ICON}
                    text={creditsText}
                />
            )}
            {accountsText && (
                <MetaRow
                    iconPath={ACCOUNTS_ICON}
                    text={accountsText}
                />
            )}
        </div>
    );
}

type TranslateFn = ReturnType<typeof useTranslation>['t'];

function getMetaTexts(planKey: string, t: TranslateFn) {
    const meta = PLAN_META[planKey];

    if (!meta) {
        return { creditsText: null, accountsText: null };
    }

    const creditsText =
        meta.creditsKey === 'creditsUnlimited'
            ? t('migration.meta.creditsUnlimited')
            : t('migration.meta.credits', { count: meta.creditsCount ?? 0 });
    const accountsText = t(`migration.meta.${meta.accountsKey}`);

    return { creditsText, accountsText };
}

interface MigrationPlanCardProps {
    billingPeriod: BillingPeriod;
    currentPlan: string;
    discountDurationMonths: number;
    discountPercent: number;
    isFoundersEligible: boolean;
    isLoading: boolean;
    onSelect: (priceId: string) => void;
    plan: MigrationPlan;
}

export const MigrationPlanCard = memo((props: MigrationPlanCardProps) => {
    const {
        billingPeriod,
        currentPlan,
        discountDurationMonths,
        discountPercent,
        isFoundersEligible,
        isLoading,
        onSelect,
        plan,
    } = props;
    const { t, i18n, ready: isReady } = useTranslation('subscription');

    const [isExpanded, setIsExpanded] = useState(false);
    const [isProLockedDialogOpen, setIsProLockedDialogOpen] = useState(false);

    const { priceId, displayPrice, isYearly } = getBillingValues(
        billingPeriod,
        plan,
    );

    const currentLanguage = i18n.language;

    const planFeatures = useMemo<string[]>(() => {
        if (!isReady) {
            return [];
        }

        const list = i18n.getResource(
            currentLanguage,
            'subscription',
            `migration.planFeatures.${plan.key}`,
        );

        return Array.isArray(list) ? (list as string[]) : [];
    }, [plan.key, i18n, isReady, currentLanguage]);

    const visibleFeatures = isExpanded
        ? planFeatures
        : planFeatures.slice(0, INITIAL_FEATURES_COUNT);

    const hasMoreFeatures = planFeatures.length > INITIAL_FEATURES_COUNT;
    const isShowFade = !isExpanded && hasMoreFeatures;

    const { creditsText, accountsText } = getMetaTexts(plan.key, t);

    const isPro = plan.key === 'pro';
    const isPremium = plan.key === 'premium';
    const isCurrent = plan.is_current;
    const isProLocked = isPro && !plan.eligible && !isCurrent;
    // Free trial badge: only for users who have NEVER subscribed.
    // Backend signals this via current_plan === 'none'. Returning customers
    // (current_plan === 'inactive' or 'legacy' or any plan key) already used
    // their trial and pay immediately on re-subscribe.
    const isTrialEligible = currentPlan === 'none';

    const handleSelect = useCallback(() => {
        if (isProLocked) {
            setIsProLockedDialogOpen(true);

            return;
        }

        onSelect(priceId);
    }, [isProLocked, onSelect, priceId]);

    const handleCloseProDialog = useCallback(() => {
        setIsProLockedDialogOpen(false);
    }, []);

    const handleToggleFeatures = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const isShowDiscount = isFoundersEligible && discountPercent > 0;

    let discountedPrice: string | null = null;

    if (isShowDiscount) {
        discountedPrice =
            billingPeriod === 'monthly'
                ? computeDiscountedMonthly(displayPrice ?? '0', discountPercent)
                : computeDiscountedYearly(displayPrice ?? '0', plan.key);
    }

    const periodLabel = isYearly
        ? t('migration.perYear')
        : t('migration.perMonth');
    const readMoreLabel = isExpanded
        ? t('migration.readLess')
        : t('migration.readMore');
    const buttonLabel = isCurrent ? t('migration.currentPlan') : t('subscribe');

    const rootClassName = cn(cls.root, {
        [cls.highlighted]: isPremium,
        [cls.current]: isCurrent,
    });

    const buttonClassName = cn(cls.selectButton, {
        [cls.currentButton]: isCurrent,
    });

    const featuresWrapperClassName = cn(cls.featuresWrapper, {
        [cls.featuresCollapsed]: !isExpanded,
    });

    return (
        <>
            <div className={rootClassName}>
                {isPremium && (
                    <span className={cls.popularBadge}>
                        {t('migration.mostPopular')}
                    </span>
                )}

                {isTrialEligible && (
                    <Tag className={cls.trialBadge}>
                        {t('migration.freeTrial')}
                    </Tag>
                )}

                <h3 className={cls.planName}>{plan.name}</h3>

                <PriceBlock
                    discountedPrice={discountedPrice}
                    displayPrice={displayPrice}
                    periodLabel={periodLabel}
                />

                <MetaBox
                    accountsText={accountsText}
                    creditsText={creditsText}
                />

                {isShowDiscount && (
                    <div className={cls.discountTag}>
                        {t('migration.foundersClubTag', {
                            discount: discountPercent,
                            months: discountDurationMonths,
                        })}
                    </div>
                )}

                <p className={cls.description}>
                    {t(`migration.planDescription.${plan.key}`)}
                </p>

                <div className={cls.includesLabel}>
                    {t('planDescriptionTitle')}:
                </div>

                <div className={featuresWrapperClassName}>
                    <ul className={cls.features}>
                        {visibleFeatures.map((text) => (
                            <FeatureRow
                                key={`${plan.key}-${text.slice(0, 32)}`}
                                text={text}
                            />
                        ))}
                    </ul>
                    {isShowFade && <span className={cls.featuresFade} />}
                </div>

                {hasMoreFeatures && (
                    <button
                        className={cls.readMoreDivider}
                        onClick={handleToggleFeatures}
                        type="button"
                    >
                        <span className={cls.readMoreLine} />
                        <span className={cls.readMoreText}>
                            {readMoreLabel}
                        </span>
                        <span
                            className={cn(
                                cls.readMoreLine,
                                cls.readMoreLineRight,
                            )}
                        />
                    </button>
                )}

                <Button
                    block
                    className={buttonClassName}
                    disabled={isCurrent || isLoading}
                    loading={isLoading}
                    onClick={handleSelect}
                    size="large"
                    type="default"
                >
                    {buttonLabel}
                </Button>
            </div>

            <Modal
                centered
                footer={null}
                onCancel={handleCloseProDialog}
                open={isProLockedDialogOpen}
                title={t('migration.proLocked.title')}
            >
                <p>
                    {plan.eligibility_message ||
                        t('migration.proLocked.message')}
                </p>
                <div style={{ textAlign: 'right', marginTop: 16 }}>
                    <Button
                        onClick={handleCloseProDialog}
                        type="primary"
                    >
                        {t('migration.proLocked.close')}
                    </Button>
                </div>
            </Modal>
        </>
    );
});

MigrationPlanCard.displayName = 'MigrationPlanCard';
